// coolCast — slight blue-cyan tone shift, the Polaroid SX-70 look.
//
// History: previous filter recipes used warmCast (orange/amber hue rotate) for
// a sun-soaked disposable-camera feel. F-wave β user feedback was "cool
// Polaroid, super saturated" — Polaroid SX-70 film has a characteristic
// cool blue-cyan cast in shadows and mid-tones (well documented), and that's
// what reads as "instant film" to most people. warmCast → coolCast swap.
//
// Two paths (same shape as warmCast):
//   1. Fast path: ctx.filter = 'hue-rotate(8deg) saturate(108%)' then
//      drawImage(self). GPU-accelerated on Chrome/Safari; ~free.
//      Positive hue rotate shifts oranges → yellows → greens → cyans; we use a
//      small +8° so faces don't go alien, just slightly cooler.
//   2. Per-pixel fallback: r -= 6, b += 9. Used when ctx.filter is unsupported
//      (older Firefox, OffscreenCanvas in some workers, test envs).

import { type FilterContext, clamp, eachPixel, getImageData, putImageData } from './_helpers'

export type CoolCastOpts = {
  // Forces the per-pixel branch even when ctx.filter is supported. Tests set
  // this. Production callers leave it default.
  forcePerPixel?: boolean
}

const COOL_R = -6
const COOL_B = 9

export function coolCast(c: FilterContext, opts: CoolCastOpts = {}): void {
  const supportsFilter = !opts.forcePerPixel && typeof c.ctx.filter === 'string'
  if (supportsFilter) {
    const prev = c.ctx.filter
    c.ctx.filter = 'hue-rotate(8deg) saturate(108%)'
    c.ctx.drawImage(c.ctx.canvas as CanvasImageSource, 0, 0)
    c.ctx.filter = prev
    return
  }

  coolCastPerPixel(c)
}

// Exposed for direct invocation in tests + the orchestrator's deterministic
// recap render path.
export function coolCastPerPixel(c: FilterContext): void {
  const img = getImageData(c.ctx)
  eachPixel(img, (r, g, b, a) => [clamp(r + COOL_R), g, clamp(b + COOL_B), a])
  putImageData(c.ctx, img)
}
